/* eslint-disable complexity */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Text, Link, Icon, Flex
} from 'components/ui';
import CurbsidePickupIndicator from 'components/CurbsidePickupIndicator';
import ConciergeCurbsidePickupIndicator from 'components/ConciergeCurbsidePickupIndicator';
import { renderModal, globalModals } from 'utils/globalModals';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import storeUtils from 'utils/Store';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import { BOPIS_WARNING_KEY } from 'components/GlobalModals/ChooseOptionsModal/constants';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA';

const { getBOPISCountdownMessage } = DeliveryOptionsUtils;
const { BOPIS_SERVICE_INFO_FAQS } = globalModals;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR);

function BOPIS({
    currentProduct,
    preferredStoreInfo,
    isSelected,
    displayOrderCutoffCountdown,
    reserveAndPickupClick,
    showStoreListModal,
    disabledText,
    showBCCMediaModal,
    bccMediaSpecInfo,
    isOutOfStockWithNearbyInStockAlternative,
    onClickAnalytics
}) {
    const globalModalsData = useSelector(globalModalsSelector);
    const aboutBopisModal = globalModalsData[BOPIS_SERVICE_INFO_FAQS];

    const { currentSku = {}, warnings = Empty.Array } = currentProduct;
    const isPickUpEligibleSku = currentSku?.isPickUpEligibleSku;
    const { isCurbsideEnabled, isConciergeCurbsideEnabled, isBopisable } = preferredStoreInfo;
    const isReservationNotOffered = currentSku.actionFlags?.isReservationNotOffered;
    const pickupStoreName = storeUtils.getStoreDisplayName(preferredStoreInfo);
    const availabilityStatus = currentSku.actionFlags?.availabilityStatus;
    const availabilityLabel = ExtraProductDetailsUtils.availabilityLabel(availabilityStatus);
    const availabilityText = getText(availabilityLabel);
    const isOutOfStock = ExtraProductDetailsUtils.isOutOfStock(availabilityStatus);
    const availabilityTextColor = !availabilityStatus ? null : isReservationNotOffered ? 'gray' : isOutOfStock ? 'red' : 'green';

    // Static locale strings
    const atText = getText('at');
    const inStockText = getText('inStock');
    const nearbyStoresText = getText('nearbyStores');
    const storesNearYouText = getText('storesNearYou');
    const pickUpNotOfferedText = getText('pickUpNotOffered');
    const pickUpNotOfferedForItemText = getText('pickUpNotOfferedForItem');
    const checkOtherStoresBaseText = getText('checkOtherStores');
    const inStorePickupText = getText('inStorePickup');
    const curbsidePickupText = getText('curbsidePickup');
    const curbsideConciergeText = getText('curbsideConcierge');
    const aboutBuyOnlineAndPickUpLink = getText('aboutBuyOnlineAndPickUp');

    const storeNameText = pickupStoreName || storesNearYouText;
    const bopisWarning = warnings.find(warning => warning.key === BOPIS_WARNING_KEY);
    const pickupNotOfferedText = bopisWarning?.errorMessages?.[0] || pickUpNotOfferedText;

    const pickupMessageRaw = isReservationNotOffered
        ? pickupNotOfferedText
        : isOutOfStock
            ? pickUpNotOfferedForItemText
            : currentProduct.pickupMessage && displayOrderCutoffCountdown
                ? getBOPISCountdownMessage(currentProduct.pickupMessage)
                : currentProduct.pickupMessage;
    const pickupMessage = isSelected && pickupMessageRaw;
    const pickupMessageColor = isReservationNotOffered || isOutOfStock ? 'gray' : 'green';
    const checkOtherStoresText = (isReservationNotOffered || isOutOfStock) && checkOtherStoresBaseText;

    let showCurbsidePickupIndicator, showConciergeCurbsidePickupIndicator, storePickupIndicator;

    if (isBopisable && isPickUpEligibleSku && !isOutOfStock && !isReservationNotOffered) {
        storePickupIndicator = inStorePickupText;

        if (isCurbsideEnabled || isConciergeCurbsideEnabled) {
            showCurbsidePickupIndicator = !isConciergeCurbsideEnabled && isCurbsideEnabled;
            showConciergeCurbsidePickupIndicator = isConciergeCurbsideEnabled;
        }
    }

    return (
        <>
            {disabledText ? (
                <Text
                    is='p'
                    color='gray'
                    children={disabledText}
                />
            ) : (
                <>
                    {isOutOfStockWithNearbyInStockAlternative ? (
                        <Text
                            is='p'
                            marginBottom='2px'
                            data-at={Sephora.debug.dataAt('in_stock_at_nearby_stores_label')}
                        >
                            <Text
                                fontWeight='bold'
                                color='green'
                                children={inStockText}
                            />
                            {` ${atText} `}
                            <Link
                                onClick={e => showStoreListModal(e, { callback: reserveAndPickupClick, isFromChangeMethod: true })}
                                arrowDirection='down'
                                fontWeight='bold'
                                children={nearbyStoresText}
                            />
                        </Text>
                    ) : (
                        <>
                            <Text
                                is='p'
                                marginBottom='2px'
                            >
                                <Text
                                    data-at={Sephora.debug.dataAt('pickup_stock_level_label')}
                                    fontWeight={availabilityStatus && 'bold'}
                                    color={availabilityTextColor}
                                    children={availabilityText}
                                />
                                {` ${atText} `}
                                <Link
                                    onClick={e => showStoreListModal(e, { callback: reserveAndPickupClick, isFromChangeMethod: true })}
                                    arrowDirection='down'
                                    fontWeight='bold'
                                    children={storeNameText}
                                />
                            </Text>
                            <Text
                                is='p'
                                data-at={Sephora.debug.dataAt('pickup_not_offered_label')}
                                color={pickupMessageColor}
                                children={pickupMessage}
                            />
                        </>
                    )}
                    {checkOtherStoresText && (
                        <p>
                            <Link
                                data-at={Sephora.debug.dataAt('check_other_stores_link')}
                                color='blue'
                                padding={2}
                                margin={-2}
                                onClick={e => showStoreListModal(e, { callback: reserveAndPickupClick, isFromChangeMethod: true })}
                                children={checkOtherStoresText}
                            />
                        </p>
                    )}
                    <Flex
                        alignItems='center'
                        fontSize='sm'
                        color='gray'
                        gap={2}
                        flexWrap='wrap'
                    >
                        {storePickupIndicator && (
                            <Flex
                                gap={1}
                                alignItems='center'
                            >
                                <Icon
                                    name='checkmark'
                                    size='1em'
                                />
                                {storePickupIndicator}
                            </Flex>
                        )}
                        {showCurbsidePickupIndicator && (
                            <CurbsidePickupIndicator
                                dataAt='curbside_indicator_label'
                                iconColor='gray'
                                children={curbsidePickupText}
                            />
                        )}
                        {showConciergeCurbsidePickupIndicator && (
                            <ConciergeCurbsidePickupIndicator
                                dataAt={
                                    Sephora.isMobile()
                                        ? 'concierge_curbside_indicator_stores_modal_label'
                                        : 'concierge_curbside_indicator_flyout_label'
                                }
                                iconName='checkmark'
                                iconColor='gray'
                                children={curbsideConciergeText}
                            />
                        )}
                    </Flex>
                    {aboutBuyOnlineAndPickUpLink && (
                        <Text
                            is='p'
                            marginTop={2}
                        >
                            <Link
                                display='block'
                                color='blue'
                                onClick={e => {
                                    const bccSpecInfo = {
                                        type: 'information',
                                        info: bccMediaSpecInfo
                                    };

                                    renderModal(aboutBopisModal, () => {
                                        showBCCMediaModal(e, bccSpecInfo);
                                    });

                                    const { sid } = aboutBopisModal;

                                    // When the props contains an SID, the fallback function
                                    // doesn't fire, and the analytics are not triggered.
                                    if (onClickAnalytics && sid) {
                                        onClickAnalytics(e, { bccSpecInfo });
                                    }
                                }}
                                padding={2}
                                margin={-2}
                                children={aboutBuyOnlineAndPickUpLink}
                            />
                        </Text>
                    )}
                </>
            )}
        </>
    );
}

BOPIS.propTypes = {
    currentProduct: PropTypes.object,
    preferredStoreInfo: PropTypes.object,
    isSelected: PropTypes.bool,
    displayOrderCutoffCountdown: PropTypes.bool,
    reserveAndPickupClick: PropTypes.func,
    showStoreListModal: PropTypes.func,
    disabledText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    showBCCMediaModal: PropTypes.func,
    bccMediaSpecInfo: PropTypes.string,
    isOutOfStockWithNearbyInStockAlternative: PropTypes.bool,
    onClickAnalytics: PropTypes.func
};

export default wrapFunctionalComponent(BOPIS, 'BOPIS');
